import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

const SCROLL_POSITIONS_KEY = "scrollPositions";

function getScrollPositions(): Record<string, number> {
    try {
        const stored = sessionStorage.getItem(SCROLL_POSITIONS_KEY);
        return stored ? JSON.parse(stored) : {};
    } catch {
        return {};
    }
}

function saveScrollPosition(pathname: string, position: number): void {
    try {
        const positions = getScrollPositions();
        positions[pathname] = position;
        sessionStorage.setItem(SCROLL_POSITIONS_KEY, JSON.stringify(positions));
    } catch {
        // Ignore storage errors
    }
}

function getScrollPosition(pathname: string): number | null {
    const positions = getScrollPositions();
    return positions[pathname] ?? null;
}

export function useScrollRestoration(): void {
    const { pathname } = useLocation();
    const navigationType = useNavigationType();
    const isFirstRender = useRef(true);

    useEffect(() => {
        // On route change, handle scroll behavior
        if (isFirstRender.current) {
            isFirstRender.current = false;
            // On initial page load, let browser handle it or restore if POP
            if (navigationType === "POP") {
                const savedPosition = getScrollPosition(pathname);
                if (savedPosition !== null) {
                    requestAnimationFrame(() => {
                        window.scrollTo(0, savedPosition);
                    });
                }
            }
            return;
        }

        if (navigationType === "POP") {
            // Back/forward navigation - restore scroll position
            const savedPosition = getScrollPosition(pathname);
            if (savedPosition !== null) {
                requestAnimationFrame(() => {
                    window.scrollTo(0, savedPosition);
                });
            }
        } else {
            // PUSH or REPLACE - new navigation, scroll to top
            window.scrollTo(0, 0);
        }
    }, [pathname, navigationType]);

    // Save scroll position on scroll (debounced)
    useEffect(() => {
        let timeoutId: ReturnType<typeof setTimeout>;

        const handleScroll = () => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                saveScrollPosition(pathname, window.scrollY);
            }, 100);
        };

        window.addEventListener("scroll", handleScroll, { passive: true });

        return () => {
            clearTimeout(timeoutId);
            window.removeEventListener("scroll", handleScroll);
        };
    }, [pathname]);

    // Save scroll position before unload
    useEffect(() => {
        const handleBeforeUnload = () => {
            saveScrollPosition(pathname, window.scrollY);
        };

        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, [pathname]);
}
